'use client';

import { createContext, useContext, useMemo } from 'react';
import type { TLocale } from '@/i18n/config';
import { createTranslator, type TTranslate } from '@/lib/i18n/translator';

type TI18nContext = { locale: TLocale; t: TTranslate };

const I18nContext = createContext<TI18nContext | null>(null);

/** Hands the language the server resolved down to client components. */
export function I18nProvider({ locale, children }: { locale: TLocale; children: React.ReactNode }) {
  const value = useMemo(() => ({ locale, t: createTranslator(locale) }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** The client side of `getTranslator()`: the same `t`, for the same language. */
export function useI18n(): TI18nContext {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
